/**
 * AI Recruit - Reports Page
 * Organization Settings > Reports. Visible only with reports.viewDownload
 * (see OrgSettingsLayout); the backend enforces the same capability.
 */

import React, { useState } from 'react'
import { toast } from 'react-hot-toast'
import {
  ArrowDownTrayIcon, ChartBarIcon, UsersIcon, CheckBadgeIcon, ClockIcon,
} from '@heroicons/react/24/outline'

import LoadingSpinner from '@/components/common/LoadingSpinner'
import { downloadUsageHiringReport } from '@/services/reportsApi'

const SECTIONS = [
  { icon: ChartBarIcon, title: 'Summary', text: 'Every key number, until last week vs this week, with the change.' },
  { icon: UsersIcon, title: 'Users growth', text: 'New and active users by week and month, and users by role.' },
  { icon: CheckBadgeIcon, title: 'Selections vs rejections', text: 'Outcomes, success ratio, weekly trend and a breakdown by client.' },
  { icon: ClockIcon, title: 'Time to close', text: 'Days to close a job and to the first selection, by client and job.' },
]

const ReportsPage: React.FC = () => {
  const [downloading, setDownloading] = useState(false)

  const download = async () => {
    setDownloading(true)
    try {
      await downloadUsageHiringReport()
      toast.success('Report downloaded')
    } catch (e: any) {
      toast.error(e?.response?.status === 403 ? 'You do not have permission to download reports.' : 'Failed to generate the report')
    } finally {
      setDownloading(false)
    }
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-gray-900">Reports</h2>
        <p className="text-sm text-gray-500">Download reports for your organization.</p>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <div className="flex items-start justify-between gap-4 px-5 py-4 border-b border-gray-100">
          <div>
            <h3 className="text-sm font-semibold text-gray-900">Usage &amp; Hiring Report</h3>
            <p className="text-xs text-gray-500 mt-0.5">
              Excel workbook comparing <span className="font-medium text-gray-700">until last week</span> with{' '}
              <span className="font-medium text-gray-700">this week</span> (weeks run Monday to Sunday).
            </p>
          </div>
          <button
            onClick={download}
            disabled={downloading}
            className="inline-flex items-center gap-1.5 px-3 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50 flex-shrink-0"
          >
            {downloading ? <LoadingSpinner size="sm" /> : <ArrowDownTrayIcon className="w-4 h-4" />}
            {downloading ? 'Preparing…' : 'Download Excel'}
          </button>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 p-5">
          {SECTIONS.map(({ icon: Icon, title, text }) => (
            <div key={title} className="flex items-start gap-3 rounded-lg bg-blue-50/50 border border-blue-100 px-3.5 py-3">
              <Icon className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-gray-900">{title}</p>
                <p className="text-xs text-gray-500 mt-0.5">{text}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

export default ReportsPage
